import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, Lightbulb } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Button } from '../ui/button';
import api from '../../lib/api';
import { GUIA_SECCIONES } from './guiaContenido';

const PDF = 'Guia_Administracion_de_Renta.pdf';

const Captura = ({ archivo, leyenda }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/guia/archivo/${archivo}`, { responseType: 'blob' })
      .then(r => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(''));
    return () => url && URL.revokeObjectURL(url);
  }, [archivo]);
  return (
    <figure className="mt-3">
      <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50 aspect-[16/9] flex items-center justify-center">
        {src === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          : src ? <img src={src} alt={leyenda} className="w-full h-full object-cover object-top" />
            : <span className="text-xs text-slate-400">Captura no disponible</span>}
      </div>
      <figcaption className="text-[11px] text-slate-500 mt-1 text-center italic">{leyenda}</figcaption>
    </figure>
  );
};

const Seccion = ({ s }) => (
  <AccordionItem value={s.n} className="border-slate-200" data-testid={`guia-seccion-${s.n}`}>
    <AccordionTrigger className="hover:no-underline py-3">
      <span className="flex items-center gap-3 text-left">
        <span className="w-7 h-7 rounded-full bg-[#1B4D3E] text-white text-xs font-bold flex items-center justify-center shrink-0">{s.n}</span>
        <span>
          <span className="block text-sm font-semibold text-slate-900">{s.titulo}</span>
          <span className="block text-xs text-slate-500 font-normal">{s.donde}</span>
        </span>
      </span>
    </AccordionTrigger>
    <AccordionContent>
      <ol className="space-y-2 pl-1">
        {s.pasos.map((p, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-slate-700 leading-snug">
            <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
            <span dangerouslySetInnerHTML={{ __html: p }} />
          </li>
        ))}
      </ol>
      <div className="mt-3 flex gap-2 rounded-md bg-emerald-50 border-l-4 border-emerald-400 px-3 py-2 text-xs text-emerald-900">
        <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" /> <span>{s.tip}</span>
      </div>
      {s.capturas.map(([f, l]) => <Captura key={f} archivo={f} leyenda={l} />)}
    </AccordionContent>
  </AccordionItem>
);

export const GuiaAyudaPanel = ({ open, onOpenChange }) => {
  const [bajando, setBajando] = useState(false);
  const descargar = async () => {
    setBajando(true);
    try {
      const r = await api.get(`/guia/archivo/${PDF}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = PDF; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar la guía');
    } finally {
      setBajando(false);
    }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="guia-ayuda-panel">
        <SheetHeader className="text-left">
          <SheetTitle className="text-xl text-[#1B4D3E]">Guía de uso · Administración de renta</SheetTitle>
          <SheetDescription>Las tareas del día a día, en el orden en que se hacen.</SheetDescription>
        </SheetHeader>
        <Button onClick={descargar} disabled={bajando} className="mt-4 w-full bg-[#1B4D3E] hover:bg-[#153d31]" data-testid="guia-descargar-pdf-btn">
          {bajando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Descargar guía en PDF
        </Button>
        <Accordion type="single" collapsible defaultValue="0" className="mt-4">
          {GUIA_SECCIONES.map(s => <Seccion key={s.n} s={s} />)}
        </Accordion>
      </SheetContent>
    </Sheet>
  );
};
